"use client";
import "./investment_process_slider.scss";
import classnames from "classnames";
import { ReactElement, useEffect, useState } from "react";
import { Text } from "../../../elements/text/text";
import { i18n } from "../../../model/i18n";
import { Icon } from "../../../elements/icon/icon";

const ITEMS = [
  {
    title: i18n.t("pages.advisor.investment_process_slider.step_1.title"),
    altText: i18n.t("pages.advisor.investment_process_slider.step_1.alt_text"),
    bulletPoints: [
      i18n.t("pages.advisor.investment_process_slider.step_1.desc_1"),
      i18n.t("pages.advisor.investment_process_slider.step_1.desc_2"),
      i18n.t("pages.advisor.investment_process_slider.step_1.desc_3"),
    ],
  },
  {
    title: i18n.t("pages.advisor.investment_process_slider.step_2.title"),
    altText: i18n.t("pages.advisor.investment_process_slider.step_2.alt_text"),
    bulletPoints: [
      i18n.t("pages.advisor.investment_process_slider.step_2.desc_1"),
      i18n.t("pages.advisor.investment_process_slider.step_2.desc_2"),
      i18n.t("pages.advisor.investment_process_slider.step_2.desc_3"),
    ],
  },
  {
    title: i18n.t("pages.advisor.investment_process_slider.step_3.title"),
    altText: i18n.t("pages.advisor.investment_process_slider.step_3.alt_text"),
    bulletPoints: [
      i18n.t("pages.advisor.investment_process_slider.step_3.desc_1"),
      i18n.t("pages.advisor.investment_process_slider.step_3.desc_2"),
      i18n.t("pages.advisor.investment_process_slider.step_3.desc_3"),
    ],
  },
  {
    title: i18n.t("pages.advisor.investment_process_slider.step_4.title"),
    altText: i18n.t("pages.advisor.investment_process_slider.step_4.alt_text"),
    bulletPoints: [
      i18n.t("pages.advisor.investment_process_slider.step_4.desc_1"),
      i18n.t("pages.advisor.investment_process_slider.step_4.desc_2"),
      i18n.t("pages.advisor.investment_process_slider.step_4.desc_3"),
    ],
  },
  {
    title: i18n.t("pages.advisor.investment_process_slider.step_5.title"),
    altText: i18n.t("pages.advisor.investment_process_slider.step_5.alt_text"),
    bulletPoints: [
      i18n.t("pages.advisor.investment_process_slider.step_5.desc_1"),
      i18n.t("pages.advisor.investment_process_slider.step_5.desc_2"),
      i18n.t("pages.advisor.investment_process_slider.step_5.desc_3"),
    ],
  },
  {
    title: i18n.t("pages.advisor.investment_process_slider.step_6.title"),
    altText: i18n.t("pages.advisor.investment_process_slider.step_6.alt_text"),
    bulletPoints: [
      i18n.t("pages.advisor.investment_process_slider.step_6.desc_1"),
      i18n.t("pages.advisor.investment_process_slider.step_6.desc_2"),
      i18n.t("pages.advisor.investment_process_slider.step_6.desc_3"),
    ],
  },
  {
    title: i18n.t("pages.advisor.investment_process_slider.step_7.title"),
    altText: i18n.t("pages.advisor.investment_process_slider.step_7.alt_text"),
    bulletPoints: [
      i18n.t("pages.advisor.investment_process_slider.step_7.desc_1"),
      i18n.t("pages.advisor.investment_process_slider.step_7.desc_2"),
      i18n.t("pages.advisor.investment_process_slider.step_7.desc_3"),
    ],
  },
];

export function InvestmentProcessSlider(): ReactElement {
  const [activeItem, setActiveItem] = useState(0);
  const disabledLeft = activeItem === 0;
  const disabledRight = activeItem === ITEMS.length - 1;
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && typeof navigator !== "undefined") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsTouchDevice("ontouchend" in window || navigator.maxTouchPoints > 0);
    }
  }, []);

  return (
    <section
      className="investment-process-slider"
      aria-label={i18n.t("pages.advisor.investment_process_slider.alt_text", {
        current: ITEMS[activeItem].altText,
      })}
    >
      <div className="investment-process-slider__top">
        <ul className="investment-process-slider__list">
          {ITEMS.map((item, index) => {
            const isActive = activeItem === index;

            return (
              <li
                key={item.title}
                className={classnames("investment-process-slider__item", {
                  "investment-process-slider__item--active": isActive,
                })}
                onClick={() => {
                  if (isTouchDevice) return;
                  setActiveItem((item) => {
                    if (item === ITEMS.length - 1) {
                      return 0;
                    }
                    return item + 1;
                  });
                }}
                onTouchEnd={() => {
                  setActiveItem((item) => {
                    if (item === ITEMS.length - 1) {
                      return 0;
                    }
                    return item + 1;
                  });
                }}
              >
                <div
                  className="investment-process-slider__image"
                  role="img"
                  aria-label={item.altText}
                ></div>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="investment-process-slider__bottom">
        <button
          className={classnames(
            "investment-process-slider__arrow",
            "investment-process-slider__arrow--left",
            {
              "investment-process-slider__arrow --disabled": disabledLeft,
            },
          )}
          onClick={() => {
            setActiveItem((item) => {
              return Math.max(item - 1, 0);
            });
          }}
        >
          <Icon
            name="arrow-slider-left"
            size={42}
            color={disabledLeft ? "disabled-on-default" : "accent-tinted"}
          />
        </button>
        <ul className="investment-process-slider__contents">
          {ITEMS.map((item, index) => {
            const isActive = activeItem === index;

            return (
              <li
                key={item.title}
                className={classnames("investment-process-slider__content", {
                  "investment-process-slider__content--active": isActive,
                })}
              >
                <Text
                  htmlElement="h4"
                  type="sh1-xbold"
                  color="neutral-on-surface"
                >
                  {item.title}
                </Text>
                {item.bulletPoints && (
                  <ul className="investment-process-slider__features">
                    {item.bulletPoints.map((point) => (
                      <li
                        key={point}
                        className="investment-process-slider__feature"
                      >
                        <Text
                          htmlElement="p"
                          type="body-regular"
                          color="neutral-on-surface"
                        >
                          ● {point}
                        </Text>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
        <button
          className={classnames(
            "investment-process-slider__arrow",
            "investment-process-slider__arrow--left",
            {
              "investment-process-slider__arrow--disabled": disabledRight,
            },
          )}
          onClick={() => {
            setActiveItem((item) => {
              return Math.min(item + 1, ITEMS.length - 1);
            });
          }}
        >
          <Icon
            name="arrow-slider-right"
            size={42}
            color={disabledRight ? "disabled-on-default" : "accent-tinted"}
          />
        </button>
        <ul className="investment-process-slider__bullets">
          {ITEMS.map((item, index) => {
            const isActive = activeItem === index;

            return (
              <li
                key={item.title}
                className={classnames("investment-process-slider__bullet", {
                  "investment-process-slider__bullet--active": isActive,
                })}
                onClick={() => setActiveItem(index)}
              ></li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
